// File: typescript/apps/front-end/src/main/react/editor/thought/ThoughtTextEditorLazy.tsx
//
// Lazy boundary for ThoughtTextEditor. The tiptap/prosemirror rich-text editor (~1 MB unminified
// across the editor tree) is only needed when a user actually composes an argument/reply, yet it was
// shipping in main_bundle.js on every page. Loading it through this wrapper moves the editor and its
// tiptap deps into an on-demand chunk so read-only pages (homepage, discussion, group, user) no
// longer pay for it. See the CWV plan §7 and 2026-06-08-frontend-bundle-code-splitting-implementation-plan.md.
//
// This wrapper is a drop-in replacement for the default ThoughtTextEditor import: it forwards the
// EditorHandle ref and all props, and re-exports the editor's public types so call sites only change
// the import path. It is the module that specs jest.mock() (synchronous) instead of the real editor.

import React, { Suspense, forwardRef } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { LazyControlBoundary } from "@/main/react/LazyControlBoundary";
import { RecoverableIslandFallback } from "@/main/react/RecoverableIslandFallback";
import type { Props, EditorHandle } from "@/main/react/editor/thought/ThoughtTextEditor";

export type { Props, EditorHandle } from "@/main/react/editor/thought/ThoughtTextEditor";

const ThoughtTextEditor = lazyWithRetry(
    () => import("@/main/react/editor/thought/ThoughtTextEditor"),
    "argument-text-editor",
);

// Editor-shaped placeholder so the chunk loading does not collapse/shift the surrounding layout.
function ArgumentEditorFallback() {
    return (
        <div className="w-full animate-pulse rounded-2xl border border-border/60 bg-card p-3" aria-hidden="true">
            <div className="mb-3 flex gap-2">
                <div className="h-8 w-8 rounded-md bg-muted" />
                <div className="h-8 w-8 rounded-md bg-muted" />
                <div className="h-8 w-8 rounded-md bg-muted" />
            </div>
            <div className="space-y-2">
                <div className="h-4 w-full rounded-full bg-muted" />
                <div className="h-4 w-5/6 rounded-full bg-muted" />
            </div>
        </div>
    );
}

const ThoughtTextEditorLazy = forwardRef<EditorHandle, Props>(function ThoughtTextEditorLazy(props, ref) {
    // Containment (2026-09-15): on the 82f35cf prod rollout this chunk 404'd for members mid-deploy and
    // the rejection, re-thrown by the Suspense, reached MountErrorBoundary at the DiscussionPage root:
    // the whole discussion vanished behind the recovery card because the COMPOSER could not load. The
    // boundary sits outside the Suspense so the same failure now costs the composer alone. The retry
    // card is the right fallback for an editor (unlike a decorative block, a composer that silently
    // never appears is a member wondering why they cannot reply); its Retry reloads the page, which
    // is exactly what recovers a chunk that has since become available.
    return (
        <LazyControlBoundary controlName="thought-text-editor" fallback={<RecoverableIslandFallback />}>
            <Suspense fallback={<ArgumentEditorFallback />}>
                <ThoughtTextEditor ref={ref} {...props} />
            </Suspense>
        </LazyControlBoundary>
    );
});

export default ThoughtTextEditorLazy;
